import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import api from '../services/api';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import TeacherForm from '../components/TeacherForm';
import NationalityDisplay from '../components/NationalityDisplay';
import './Teachers.css';

const Teachers = () => {
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTeacher, setEditingTeacher] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all'); // 'all', 'teacher', 'secretary'
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
    type: 'warning'
  });

  const showConfirm = (title, message, onConfirm, type = 'warning') => {
    setConfirmDialog({
      isOpen: true,
      title,
      message,
      onConfirm,
      type
    });
  };

  const closeConfirm = () => {
    setConfirmDialog({
      isOpen: false,
      title: '',
      message: '',
      onConfirm: null,
      type: 'warning'
    });
  };

  // Obtener personal (profesores y secretarios)
  const { data: staff, isLoading, error, refetch } = useQuery(
    ['staff', roleFilter],
    async () => {
      let params = {};
      
      // Si el filtro no es 'all', filtrar por rol
      if (roleFilter === 'teacher') {
        params.role = 'teacher';
      } else if (roleFilter === 'secretary') {
        params.role = 'secretary';
      }
      // Si es 'all', no agregar filtro de rol para obtener ambos
      
      const response = await api.get('/users', { params });
      return response.data;
    },
    {
      onError: (err) => {
        console.error('❌ [Teachers] Error al cargar profesores:', err);
      }
    }
  );

  // Obtener grupos para mostrar cuántos grupos tiene cada profesor
  const { data: groups } = useQuery(
    'groups',
    async () => {
      const response = await api.get('/groups', { params: { is_active: true } });
      return response.data;
    }
  );

  const deleteMutation = useMutation(
    async (teacherId) => {
      await api.put(`/users/${teacherId}`, { is_active: false });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('staff');
        toast.success('Personal desactivado correctamente');
      },
      onError: (error) => {
        toast.error(error.response?.data?.detail || 'Error al desactivar profesor');
      }
    }
  );

  const activateMutation = useMutation(
    async (teacherId) => {
      await api.put(`/users/${teacherId}`, { is_active: true });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('staff');
        toast.success('Personal activado correctamente');
      },
      onError: (error) => {
        toast.error(error.response?.data?.detail || 'Error al activar profesor');
      }
    }
  );

  const handleCreate = () => {
    setEditingTeacher(null);
    setIsModalOpen(true);
  };

  const handleEdit = (teacher) => {
    setEditingTeacher(teacher);
    setIsModalOpen(true);
  };

  const handleDelete = (teacher) => {
    const isActive = teacher.is_active;
    const action = isActive ? 'desactivar' : 'activar';
    const statusChange = isActive
      ? 'El personal quedará INACTIVO y no podrá acceder al sistema.'
      : 'El personal quedará ACTIVO y podrá acceder al sistema.';
    
    const roleText = teacher.roles && teacher.roles.length > 0
      ? teacher.roles.map(r => r.name === 'teacher' ? 'Profesor' : r.name === 'secretary' ? 'Secretario/a' : r.name).join(', ')
      : 'Personal';
    
    const title = isActive ? '⚠️ Confirmar desactivar personal' : '✅ Confirmar activar personal';
    const message = `Nombre: ${teacher.full_name}\nRol: ${roleText}\nEstado actual: ${isActive ? 'Activo' : 'Inactivo'}\n\n${statusChange}\n\n¿Estás seguro de ${action} a este miembro del personal?`;
    
    showConfirm(
      title,
      message,
      () => {
        deleteMutation.mutate(teacher.id);
      },
      isActive ? 'warning' : 'success'
    );
  };

  const handleActivate = (teacher) => {
    const roleText = teacher.roles && teacher.roles.length > 0
      ? teacher.roles.map(r => r.name === 'teacher' ? 'Profesor' : r.name === 'secretary' ? 'Secretario/a' : r.name).join(', ')
      : 'Personal';
    
    showConfirm(
      '✅ Confirmar activar personal',
      `Nombre: ${teacher.full_name}\nRol: ${roleText}\nEstado actual: Inactivo\n\nEl personal quedará ACTIVO y podrá acceder al sistema.\n\n¿Estás seguro de activar a este miembro del personal?`,
      () => {
        activateMutation.mutate(teacher.id);
      },
      'success'
    );
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingTeacher(null);
  };

  const getTeacherGroupsCount = (teacherId) => {
    if (!groups) return 0;
    return groups.filter(g => g.teacher_id === teacherId).length;
  };

  const getRoleLabel = (roles) => {
    if (!roles || roles.length === 0) return 'Sin rol';
    const roleNames = roles.map(r => r.name);
    if (roleNames.includes('teacher')) return 'Profesor';
    if (roleNames.includes('secretary')) return 'Secretario/a';
    return roles[0].name;
  };

  const filteredStaff = staff?.filter(person => {
    const searchLower = searchTerm.toLowerCase();
    return (
      person.full_name?.toLowerCase().includes(searchLower) ||
      person.email?.toLowerCase().includes(searchLower) ||
      person.username?.toLowerCase().includes(searchLower)
    );
  }) || [];

  if (isLoading) {
    return <div className="loading">Cargando personal...</div>;
  }

  if (error) {
    return (
      <div className="error-container">
        <div className="error">
          <h3>Error al cargar personal</h3>
          <p>{error.response?.data?.detail || error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={closeConfirm}
        onConfirm={confirmDialog.onConfirm || (() => {})}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type={confirmDialog.type}
      />
    <div className="teachers-page">
      <div className="page-header">
        <div>
          <h1>Gestión de Personal</h1>
          <p>Administra profesores y secretarios de la academia</p>
        </div>
        <button className="btn btn-primary" onClick={handleCreate}>
          + Nuevo Personal
        </button>
      </div>

      <div className="filters">
        <div className="filter-group">
          <input
            type="text"
            placeholder="Buscar por nombre, email o usuario..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <div className="role-filters">
            <button
              className={`filter-btn ${roleFilter === 'all' ? 'active' : ''}`}
              onClick={() => setRoleFilter('all')}
            >
              Todos
            </button>
            <button
              className={`filter-btn ${roleFilter === 'teacher' ? 'active' : ''}`}
              onClick={() => setRoleFilter('teacher')}
            >
              Profesores
            </button>
            <button
              className={`filter-btn ${roleFilter === 'secretary' ? 'active' : ''}`}
              onClick={() => setRoleFilter('secretary')}
            >
              Secretarios
            </button>
          </div>
        </div>
      </div>

      <div className="teachers-grid">
        {filteredStaff.length === 0 ? (
          <div className="empty-state">
            <p>No hay personal registrado</p>
            <button className="btn btn-primary" onClick={handleCreate}>
              Crear Primer Personal
            </button>
          </div>
        ) : (
          filteredStaff.map((person) => {
            const groupsCount = getTeacherGroupsCount(person.id);
            const roleLabel = getRoleLabel(person.roles);
            const isTeacher = person.roles?.some(r => r.name === 'teacher');
            
            return (
              <div key={person.id} className="teacher-card">
                <div className="teacher-header">
                  <div className="teacher-avatar">
                    {person.full_name?.[0]?.toUpperCase() || 'P'}
                  </div>
                  <div className="teacher-info">
                    <h3>{person.full_name}</h3>
                    <p className="teacher-email">{person.email}</p>
                    <p className="teacher-username">@{person.username}</p>
                    <p className="teacher-username"><NationalityDisplay code={person.nationality_code} /></p>
                    <span className="role-badge">{roleLabel}</span>
                  </div>
                  <span className={`badge ${person.is_active ? 'badge-success' : 'badge-danger'}`}>
                    {person.is_active ? 'Activo' : 'Inactivo'}
                  </span>
                </div>
                <div className="teacher-stats">
                  {isTeacher && (
                    <div className="stat-item">
                      <span className="stat-label">Grupos asignados:</span>
                      <span className="stat-value">{groupsCount}</span>
                    </div>
                  )}
                  {person.phone && (
                    <div className="stat-item">
                      <span className="stat-label">Teléfono:</span>
                      <span className="stat-value">{person.phone}</span>
                    </div>
                  )}
                </div>
                <div className="teacher-actions">
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => handleEdit(person)}
                  >
                    Editar
                  </button>
                  {person.is_active ? (
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={() => handleDelete(person)}
                    >
                      Desactivar
                    </button>
                  ) : (
                    <button
                      className="btn btn-success btn-sm"
                      onClick={() => handleActivate(person)}
                    >
                      Activar
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {isModalOpen && (
        <Modal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          title={editingTeacher ? 'Editar Personal' : 'Nuevo Personal'}
          closeOnOverlayClick={false}
        >
          <TeacherForm
            key={editingTeacher?.id || 'new'} // Forzar re-render cuando cambia el modo
            teacher={editingTeacher}
            onSuccess={async () => {
              handleCloseModal();
              await queryClient.invalidateQueries('staff');
              await queryClient.invalidateQueries('roles');
              // Forzar refetch inmediato
              await refetch();
            }}
            onCancel={handleCloseModal}
          />
        </Modal>
      )}
    </div>
    </>
  );
};

export default Teachers;

